'use client';

import { createContext, useContext, useEffect, useMemo, useState, useRef, useCallback, type ReactNode } from 'react';
import { useRouter } from 'next/navigation';
import { Command } from 'cmdk';
import { useAppData } from '@/lib/data-context';
import { getVisibleRail, type SessionUser } from '@/lib/rail';
import type { RailModule } from '@/lib/rail-config';
import type { SystemRole } from '@/lib/rail-rbac';
import { commandShortcutLabel } from '@/lib/platform';

// ── Context so the nav button can open the palette ──────────────
interface CommandPaletteContextValue {
  open: boolean;
  setOpen: (v: boolean) => void;
}

const CommandPaletteContext = createContext<CommandPaletteContextValue>({
  open: false,
  setOpen: () => {},
});

export function useCommandPalette() {
  return useContext(CommandPaletteContext);
}

// ── Types ───────────────────────────────────────────────────────
interface NavAction { type: 'nav'; id: string; label: string; hint?: string; keywords?: string; href: string }
interface CreateAction { type: 'create'; id: string; label: string; href: string }
interface PatientResult { type: 'patient'; id: string; label: string; expediente?: string; href: string }
type Action = NavAction | CreateAction | PatientResult;

// Optional sub-labels for a couple of modules, in Spanish: they are shown
// next to the label and are also searchable (iL31: Inicio read "Dashboard").
const NAV_HINTS: Record<string, string> = {
  home: 'Hoy',
  citas: 'Agenda',
  notas: 'Notas médicas',
};

// Search-only aliases (matched, never shown). P94 renamed the menu labels
// Inbox → Bandeja and Tickets → Cobro; staff who type the old word from
// habit still land on the module. The v1 English hints stay findable too.
const NAV_KEYWORDS: Record<string, string> = {
  home: 'Dashboard',
  citas: 'Calendario',
  inbox: 'Inbox',
  tickets: 'Tickets',
};

// Pages that open their create composer from a `?new=1` query param
// (verified: only these three read it). Sourced as the create actions;
// gating flows from rail-config (the module must be visible AND its
// quickAdd allowed for the user). Per-module quick-add side panels +
// `/module/new` routes — and citas/gastos/etc. — land in M4.
const NEW_PARAM_PAGES: Record<string, string> = {
  pacientes: 'Nuevo paciente',
  tickets: 'Nuevo ticket',
  notas: 'Nueva nota',
};

/**
 * railToPaletteActions — derive the palette's "Ir a" + "Crear" entries
 * from the role-filtered rail (single source of truth, spec D14). Nav
 * points at module hrefs (which exist today); sub-page jump-to is added
 * in M3 once the sub-routes exist. Create entries are limited to pages
 * that honor `?new=1` and whose module quickAdd survived role-gating.
 * Pure + exported for unit testing.
 */
export function railToPaletteActions(rail: RailModule[]): { nav: NavAction[]; create: CreateAction[] } {
  const nav: NavAction[] = rail.map((m) => ({
    type: 'nav',
    id: `nav-${m.id}`,
    label: m.label,
    hint: NAV_HINTS[m.id],
    keywords: NAV_KEYWORDS[m.id],
    href: m.href,
  }));
  const create: CreateAction[] = rail
    .filter((m) => m.quickAdd && NEW_PARAM_PAGES[m.id])
    .map((m) => ({
      type: 'create',
      id: `new-${m.id}`,
      label: NEW_PARAM_PAGES[m.id],
      href: `/${m.id}?new=1`,
    }));
  return { nav, create };
}

function isTypingTarget(el: EventTarget | null): boolean {
  if (!el || !(el instanceof HTMLElement)) return false;
  if (el.isContentEditable) return true;
  const tag = el.tagName;
  return tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT';
}

/** Did a page-level `/` handler claim the key? The one signal every binder
 *  gives: by the end of the keydown dispatch focus sits in a text field
 *  (/inbox, /pacientes, /gastos, the tickets historial and TicketsLog all
 *  `.focus()` their search box). `defaultPrevented` is NOT that signal —
 *  useRowKeyboardNav preventDefaults `/` on `document` (ahead of this window
 *  listener whatever the registration order) even when its page passes no
 *  onFocusSearch, which left `/` dead on the /tickets cockpit. Pure; exported
 *  for tests. */
export function focusInTextField(active: Element | null): boolean {
  return isTypingTarget(active);
}

/** Tallest the result list gets (px); the CSS default below. */
export const PALETTE_LIST_MAX = 400;

/** Max height for the result list so it ends above the bottom of what is
 *  actually visible. The iOS keyboard shrinks the VISUAL viewport, not the
 *  layout one, so on a sideways iPhone the keyboard bar covered the third
 *  "Crear" row and every patient result (landscape audit L7). `vvBottom` is
 *  visualViewport.offsetTop + height, `listTop` the list's top edge, both in
 *  layout-viewport px. null = the CSS default already fits. Never below two
 *  44pt rows, so the list stays usable. Pure; exported for tests. */
export function paletteListCap(vvBottom: number, listTop: number): number | null {
  const room = Math.floor(vvBottom - listTop - 16);
  if (room >= PALETTE_LIST_MAX) return null;
  return Math.max(88, room);
}

// ── Provider + Palette ──────────────────────────────────────────
export function CommandPaletteProvider({ children }: { children: ReactNode }) {
  const [open, setOpen] = useState(false);
  return (
    <CommandPaletteContext.Provider value={{ open, setOpen }}>
      {children}
      <CommandPaletteInner open={open} setOpen={setOpen} />
    </CommandPaletteContext.Provider>
  );
}

function CommandPaletteInner({ open, setOpen }: { open: boolean; setOpen: (v: boolean) => void }) {
  const [search, setSearch] = useState('');
  const [patients, setPatients] = useState<PatientResult[]>([]);
  const router = useRouter();
  const searchTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const { user } = useAppData();
  const paletteRef = useRef<HTMLDivElement>(null);
  const [listCap, setListCap] = useState<number | null>(null);

  // Keep the list above the keyboard (L7): re-fit whenever the visual
  // viewport changes (keyboard up/down, rotation, pinch) while open.
  useEffect(() => {
    if (!open || typeof window === 'undefined' || !window.visualViewport) return;
    const vv = window.visualViewport;
    const fit = () => {
      const input = paletteRef.current?.querySelector('[cmdk-input]');
      if (!input) return;
      setListCap(paletteListCap(vv.offsetTop + vv.height, input.getBoundingClientRect().bottom));
    };
    fit();
    // Once more after the 220ms pop-in: its scale skews the first measure.
    const settle = window.setTimeout(fit, 240);
    vv.addEventListener('resize', fit);
    vv.addEventListener('scroll', fit);
    return () => {
      window.clearTimeout(settle);
      vv.removeEventListener('resize', fit);
      vv.removeEventListener('scroll', fit);
      setListCap(null);
    };
  }, [open]);

  const { nav: visibleNavActions, create: visibleCreateActions } = useMemo(() => {
    const sessionUser: SessionUser = {
      id: user?.id ?? '',
      // Default to least privilege while the session loads.
      system_role: (user?.system_role as SystemRole) ?? 'staff',
    };
    return railToPaletteActions(getVisibleRail(sessionUser));
  }, [user?.id, user?.system_role]);

  // Cmd+K / Ctrl+K toggles; `/` opens (unless typing); Escape closes.
  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'k') {
        e.preventDefault();
        setOpen(!open);
        if (!open) {
          try { window.posthog?.capture('command_palette_opened', { trigger: 'cmdk' }); } catch {}
        }
      } else if (e.key === '/' && !open && !e.metaKey && !e.ctrlKey && !e.altKey && !isTypingTarget(e.target)) {
        // GitHub-style `/` to search (spec §6.4). Bails while typing so it
        // never hijacks a literal slash in a field — and yields to a page that
        // binds `/` to its own search box (E4: the palette used to open on top
        // and its autoFocus stole the focus the page had just placed). The
        // page's listener may run before or after this one (document vs
        // window, registration order), so the decision waits for the dispatch
        // to end and looks only at where focus landed — focusInTextField says
        // why defaultPrevented is not consulted. ⌘K is untouched.
        e.preventDefault();
        setTimeout(() => {
          if (focusInTextField(document.activeElement)) return;
          setOpen(true);
          try { window.posthog?.capture('command_palette_opened', { trigger: 'slash' }); } catch {}
        }, 0);
      } else if (e.key === 'Escape' && open) {
        e.preventDefault();
        setOpen(false);
      }
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [open, setOpen]);

  // Reset on close
  useEffect(() => {
    if (!open) { setSearch(''); setPatients([]); }
  }, [open]);

  // Debounced patient search via API
  const searchPatients = useCallback((q: string) => {
    if (searchTimer.current) clearTimeout(searchTimer.current);
    if (q.trim().length < 2) { setPatients([]); return; }
    searchTimer.current = setTimeout(async () => {
      try {
        const res = await fetch(`/api/patients-search?q=${encodeURIComponent(q.trim())}`);
        if (!res.ok) { setPatients([]); return; }
        const data = await res.json();
        if (Array.isArray(data)) {
          setPatients(data.slice(0, 8).map((p: any) => ({
            type: 'patient' as const,
            id: `patient-${p.id}`,
            label: p.name,
            expediente: p.expediente ? `#${p.expediente}` : undefined,
            href: `/pacientes?patient=${p.id}`,
          })));
        }
      } catch { setPatients([]); }
    }, 200);
  }, []);

  useEffect(() => {
    searchPatients(search);
    return () => { if (searchTimer.current) clearTimeout(searchTimer.current); };
  }, [search, searchPatients]);

  const handleSelect = (action: Action) => {
    try {
      window.posthog?.capture('command_palette_action', {
        action_type: action.type,
        action_id: action.type === 'patient' ? 'patient' : action.id,
      });
    } catch {}
    setOpen(false);
    router.push(action.href);
  };

  if (!open) return null;

  return (
    <>
      <div
        onClick={() => setOpen(false)}
        aria-hidden="true"
        style={{
          position: 'fixed', inset: 0,
          background: 'rgba(20, 20, 19, 0.4)',
          zIndex: 200,
          animation: 'overlay-fade 220ms var(--ease-quiet)',
        }}
      />
      <div
        ref={paletteRef}
        className="palette"
        style={{
          position: 'fixed', top: '12vh', left: '50%', transform: 'translateX(-50%)',
          width: 'min(560px, 92vw)', zIndex: 210,
          animation: 'palette-pop 220ms var(--ease-spring)',
          fontFamily: 'var(--font-sans)',
        }}
      >
        {/* v2 cmdk skin — overrides the v1 [cmdk-*] block in globals.css
            for this open palette only. Keeps the cmdk library's
            selection / filter / a11y behavior intact. */}
        <style>{`
          .palette [cmdk-group-heading] {
            padding: 10px 14px 4px;
            font-family: var(--font-sans);
            font-size: 10.5px;
            letter-spacing: 1px;
            text-transform: uppercase;
            color: var(--ink-4);
          }
          .palette [cmdk-item] {
            display: flex;
            align-items: center;
            gap: 10px;
            padding: 8px 12px;
            border-radius: 6px;
            cursor: pointer;
            font-size: 13px;
            color: var(--ink);
            transition: background-color 120ms var(--ease-quiet);
          }
          .palette [cmdk-item][data-selected="true"] {
            background: var(--accent-bg);
            color: var(--ink);
          }
          /* Touch (the phone/iPad header search opens this): finger-sized
             rows, and a 16px field so iOS Safari doesn't zoom the page. */
          @media (pointer: coarse) {
            .palette [cmdk-item] { min-height: 44px; }
            .palette [cmdk-input] { font-size: 16px !important; }
          }
          /* A sideways phone is ~400pt tall: 12vh of air above the
             panel is a row of results lost (L7). */
          @media (max-height: 500px) {
            .palette { top: max(8px, 4vh) !important; }
          }
          /* The shortcut only means something with a keyboard and a mouse;
             on a touch iPad it was noise (iL31). */
          .palette .palette-kbd { display: none; }
          @media (hover: hover) and (pointer: fine) {
            .palette .palette-kbd { display: inline; }
          }
        `}</style>
        <Command
          label="Buscar y navegar"
          shouldFilter
          style={{
            background: 'var(--surface)',
            border: '1px solid var(--hairline-strong)',
            borderRadius: '10px',
            boxShadow: '0 24px 64px rgba(20,20,19,0.18), 0 2px 8px rgba(20,20,19,0.08)',
            overflow: 'hidden',
            fontFamily: 'var(--font-sans)',
          }}
        >
          <Command.Input
            value={search}
            onValueChange={setSearch}
            placeholder="Buscar paciente o navegar…"
            autoFocus
            style={{
              width: '100%', border: 'none', outline: 'none',
              padding: '14px 18px', fontSize: 14,
              fontFamily: 'var(--font-sans)', color: 'var(--ink)',
              background: 'transparent',
              borderBottom: '1px solid var(--hairline)',
            }}
          />
          <Command.List
            style={{
              // 400px, less on a short screen (12vh above the panel, ~56px
              // input + 16px air), less again above the keyboard (listCap).
              maxHeight: listCap != null ? `${listCap}px` : `min(${PALETTE_LIST_MAX}px, calc(100dvh - 12vh - 72px))`,
              overflowY: 'auto',
              padding: '6px',
            }}
          >
            <Command.Empty style={{ padding: '24px 20px', fontSize: 12, color: 'var(--ink-4)', textAlign: 'center' }}>
              Sin resultados
            </Command.Empty>

            {patients.length > 0 && (
              <Command.Group heading="Pacientes">
                {patients.map((p) => (
                  <Command.Item key={p.id} value={`${p.label} ${p.expediente ?? ''}`} onSelect={() => handleSelect(p)}>
                    <span>{p.label}</span>
                    {p.expediente && (
                      <span style={{
                        marginLeft: 'auto', fontFamily: 'var(--font-mono)',
                        fontVariantNumeric: 'tabular-nums', fontSize: 11, color: 'var(--ink-4)',
                      }}>
                        {p.expediente}
                      </span>
                    )}
                  </Command.Item>
                ))}
              </Command.Group>
            )}

            {visibleCreateActions.length > 0 && (
              <Command.Group heading="Crear">
                {visibleCreateActions.map((a) => (
                  <Command.Item key={a.id} value={a.label} onSelect={() => handleSelect(a)}>
                    {a.label}
                  </Command.Item>
                ))}
              </Command.Group>
            )}

            <Command.Group heading="Ir a">
              {visibleNavActions.map((a) => (
                <Command.Item key={a.id} value={`${a.label} ${a.hint ?? ''} ${a.keywords ?? ''}`} onSelect={() => handleSelect(a)}>
                  <span>{a.label}</span>
                  {a.hint && (
                    <span style={{ marginLeft: 'auto', fontSize: 11, color: 'var(--ink-4)' }}>
                      {a.hint}
                    </span>
                  )}
                </Command.Item>
              ))}
            </Command.Group>
          </Command.List>

          <div style={{
            display: 'flex', justifyContent: 'space-between',
            padding: '8px 16px',
            borderTop: '1px solid var(--hairline)',
            fontSize: 11, color: 'var(--ink-4)',
            fontFamily: 'var(--font-sans)',
          }}>
            <span className="kbd-hint">↵ abrir · ↑↓ navegar</span>
            <span>
              Buscar o ir a…
              <span className="palette-kbd">{' · '}{commandShortcutLabel()}</span>
            </span>
          </div>
        </Command>
      </div>
    </>
  );
}

// Declare posthog on window for type-safety
declare global {
  interface Window {
    posthog?: { capture: (event: string, props?: Record<string, unknown>) => void };
  }
}
